// types.ts
export interface PricingData {
  id: string
  name: string
  permissions: {
    curriculum?: boolean
    roleplay?: boolean
    discussion?: boolean
    analysis?: boolean
  }
  sale_prices: Record<string, string>
}

export interface PricingCardProps {
  data: any
  discountRate: string
  originalPrice: string
  totalPriceLabel: string
  discountPriceLabel?: string
  isB2B: boolean
  currency: string
  isPopular?: any
  onPurchase: (id: string) => void
  premiumDiscountPrice?: any
  premiumDiscountPriceLabel?: string
  isPrimary?: any
}

import { BUTTON_SIZE, BUTTON_TYPE, COLORS, DSButton } from '@repo/design-system/index'
import { QIcon_bar_graph, QIcon_book, QIcon_person, QIcon_phone, QIcon_topic } from '@repo/icons/index'
import { n_pricing, n_subscribe, s_pricing } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'

const iconMap = {
  curriculum: <QIcon_book width={20} height={20} color={COLORS.GRAY400} />,
  roleplay: <QIcon_person width={20} height={20} color={COLORS.GRAY400} />,
  discussion: <QIcon_topic width={20} height={20} color={COLORS.GRAY400} />,
  analysis: <QIcon_bar_graph width={20} height={20} color={COLORS.GRAY400} />,
  voip: <QIcon_phone width={20} height={20} color={COLORS.GRAY400} />,
} as const

const PricingPackage = ({ title }) => {
  if (title) {
    return (
      <div className="rounded-[4px] bg-blue-50 text-blue-500 border-1 border-blue-200 Caption-12Bd px-[6px]">
        {title}
      </div>
    )
  } else {
    return null
  }
}

export function AITutorPricingCard({
  data,
  discountRate,
  originalPrice,
  totalPriceLabel,
  discountPriceLabel,
  isB2B,
  currency,
  onPurchase,
  isPopular,
  premiumDiscountPrice,
  premiumDiscountPriceLabel,
  isPrimary,
}: PricingCardProps) {
  const features = Object.keys(iconMap)
    .filter((key) => data.permissions[key])
    .map((key) => key as keyof typeof iconMap)

  const { t, isLocaleKO } = useTranslation()

  const renderUsingPeriod = (pricingData) => {
    if (pricingData) {
      const billingPeriodUnit = pricingData.billing_period_unit
      const billingPeriodNumber = pricingData.billing_period_number

      if (isLocaleKO) {
        if (billingPeriodUnit === 'YEAR') {
          return `${billingPeriodNumber}년`
        } else if (billingPeriodUnit === 'MONTH') {
          return `${billingPeriodNumber}개월`
        } else if (billingPeriodUnit === 'DAY') {
          return `${billingPeriodNumber}일`
        }
      } else {
        return `${billingPeriodNumber} ${billingPeriodUnit}`
      }
    }
  }

  return (
    <div className="border-1 rounded-[8px] relative py-[20px] px-[16px] w-full flex flex-col justify-between">
      <div className="absolute flex flex-row gap-x-[4px] top-[-10px] left-[8px]">
        {isPrimary && <PricingPackage title={t(s_pricing.card_popular)} />}

        {isB2B && premiumDiscountPrice && (
          <PricingPackage title={t(n_pricing.ai_option.sale_amount(premiumDiscountPriceLabel))} />
        )}
        {!isB2B && premiumDiscountPrice && (
          <PricingPackage title={t(s_pricing.benefit_list2(premiumDiscountPriceLabel))} />
        )}
      </div>

      <div>
        <div className="flex justify-between">
          <div className="flex flex-col">
            <span className="text-[20px] font-bold text-gray-800 leading-[28px]">{data.name}</span>
            <span className="Body2-14Md text-gray-500">{renderUsingPeriod(data)}</span>
          </div>
          <div className="text-right">
            <div className="flex justify-end items-center gap-1">
              <span className="font-semibold text-red-500">{discountRate}</span>
              <span className="text-sm text-gray-400 line-through">{originalPrice}</span>
            </div>
            <div className="Title1-20Bd">{totalPriceLabel}</div>
            {!['KRW', 'USD'].includes(currency) && (
              <div className="text-sm text-gray-500">({data.sale_prices[currency]})</div>
            )}
          </div>
        </div>

        <hr className="my-[20px]" />

        <div className="flex flex-col gap-8">
          {features.map((featureKey) => (
            <div key={featureKey} className="flex items-center gap-x-[8px] text-gray-700 Body2-14Md">
              {iconMap[featureKey]}
              <span>{t(n_subscribe.select[`benefit_${featureKey}`])}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-[16px]">
        <DSButton
          type={isPrimary ? BUTTON_TYPE.SOLID : BUTTON_TYPE.OUTLINE}
          size={BUTTON_SIZE.MD}
          customBorderColor={COLORS.PURPLE600}
          onClick={() => onPurchase(data.id)}
        >
          <DSButton.Label>{t(n_subscribe.tab.btn_pay)}</DSButton.Label>
        </DSButton>
      </div>
    </div>
  )
}

export default AITutorPricingCard
